import { H3, H5, Text } from '@blueprintjs/core';
import { type ReactNode } from 'react';

import { BooleanTag, Table } from '../../shared';
import { Box, Flex } from '../../shared/components';
import {
  type AccountLimitIncrease,
  type ApiRateLimitIncrease,
  type DataExportSummary,
  formatLimit,
  type GeneralAccountInfoResponse,
  getDataExportDestinationKindName,
  type IEapSegment,
  type IntegrationItem,
  type OpenTicketsMap,
  type RouteRateLimitsData,
} from '../model';

type RequestState = {
  isFetching: boolean;
  error?: Error;
};

export type IGeneralAcctInfoPanelProps = {
  generalData?: GeneralAccountInfoResponse | null;
  // Each section has its own request state so a slow/failing section renders its
  // own loading/error and never blocks or takes down the others.
  launchdarklyRequest?: RequestState; // EAP segments + route rate limits
  zendeskRequest?: RequestState;
  integrationsRequest?: RequestState;
  supportEntitlementRequest?: RequestState;
  dataExportRequest?: RequestState;
  resourceLimits?: readonly AccountLimitIncrease[] | null;
  resourceLimitsRequest?: RequestState;
  rateLimitIncreases?: readonly ApiRateLimitIncrease[] | null;
  rateLimitIncreasesRequest?: RequestState;
};

function renderTicketMap(tickets: OpenTicketsMap, label: string): ReactNode {
  if (tickets === undefined || tickets === null || typeof tickets !== 'object' || Object.keys(tickets).length === 0) {
    return (
      <tr>
        <td>{label}</td>
        <td>—</td>
      </tr>
    );
  }
  return (
    <tr>
      <td>{label}</td>
      <td>
        <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
          {Object.entries(tickets).map(([id, info]) => (
            <li key={id}>
              <a
                href={`https://launchdarklysupport.zendesk.com/agent/tickets/${id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                #{id}
              </a>
              {info?.subject !== undefined && info?.subject !== null && info.subject !== '' ? ` — ${info.subject}` : ''}
              {info?.status !== undefined && info?.status !== null && info.status !== '' ? (
                <span style={{ color: 'var(--text-muted, #666)', fontSize: '0.9em' }}> [{info.status}]</span>
              ) : null}
              {info?.created_at !== undefined && info?.created_at !== null && info.created_at !== '' ? (
                <span style={{ color: 'var(--text-muted, #666)', fontSize: '0.9em' }}>
                  {' '}
                  ({new Date(info.created_at).toLocaleDateString()})
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
}

function renderZendesk(generalData?: GeneralAccountInfoResponse | null, request?: RequestState): ReactNode {
  const heading = (
    <tr>
      <td colSpan={2}>
        <H5 style={{ marginTop: '20px', marginBottom: 0 }}>Zendesk / Account info</H5>
      </td>
    </tr>
  );
  // Independent loading/error for just the Zendesk request (rendered as rows so it
  // stays inside the Account Support table); never gated on any other section.
  if (request && request.error) {
    return (
      <>
        {heading}
        <tr>
          <td colSpan={2}>
            <Text className="error-message">Unable to load Zendesk info: {request.error.message}</Text>
          </td>
        </tr>
      </>
    );
  }
  if (request && request.isFetching) {
    return (
      <>
        {heading}
        <tr>
          <td colSpan={2}>
            <Text>Loading Zendesk info...</Text>
          </td>
        </tr>
      </>
    );
  }
  const z = generalData?.zendesk;
  if (!z) {
    return null;
  }
  const hasAny =
    (z.supportTier !== undefined && z.supportTier !== null) ||
    (z.csm !== undefined && z.csm !== null) ||
    (z.ae !== undefined && z.ae !== null) ||
    (z.tam !== undefined && z.tam !== null) ||
    (z.stampTickets !== undefined && z.stampTickets !== null) ||
    (z.openTickets !== undefined && z.openTickets !== null) ||
    // Match the original class component exactly: focusAccount presence keys off
    // `!== undefined` only, so a `focusAccount: null` with no other fields still
    // renders the Zendesk header (keeps this refactor output-identical).
    z.focusAccount !== undefined;
  if (!hasAny) {
    return null;
  }
  return (
    <>
      {heading}
      {z.supportTier !== undefined && z.supportTier !== null && (
        <tr>
          <td>Support tier</td>
          <td>{z.supportTier ? z.supportTier.charAt(0).toUpperCase() + z.supportTier.slice(1).toLowerCase() : '—'}</td>
        </tr>
      )}
      {z.csm !== undefined && z.csm !== null && (
        <tr>
          <td>CSM</td>
          <td>{z.csm || '—'}</td>
        </tr>
      )}
      {z.ae !== undefined && z.ae !== null && (
        <tr>
          <td>AE</td>
          <td>{z.ae || '—'}</td>
        </tr>
      )}
      {z.tam !== undefined && z.tam !== null && (
        <tr>
          <td>TAM</td>
          <td>{z.tam || '—'}</td>
        </tr>
      )}
      {z.stampTickets !== undefined &&
        z.stampTickets !== null &&
        typeof z.stampTickets === 'object' &&
        renderTicketMap(z.stampTickets as OpenTicketsMap, 'STAMP tickets')}
      {z.openTickets !== undefined &&
        z.openTickets !== null &&
        typeof z.openTickets === 'object' &&
        renderTicketMap(z.openTickets, 'Open tickets')}
      {z.focusAccount !== undefined && z.focusAccount !== null && (
        <tr>
          <td>Focus account</td>
          <td>{z.focusAccount ? 'Yes' : 'No'}</td>
        </tr>
      )}
    </>
  );
}

function renderRouteRateLimits(generalData?: GeneralAccountInfoResponse | null): ReactNode {
  const rrl = generalData?.routeRateLimits;
  if (!rrl || !Array.isArray(rrl.routeLimits) || rrl.routeLimits.length === 0) {
    if (rrl) {
      const hasDefaultRead = rrl.defaultReadLimit !== undefined && rrl.defaultReadLimit !== null;
      const hasDefaultWrite = rrl.defaultWriteLimit !== undefined && rrl.defaultWriteLimit !== null;
      if (hasDefaultRead || hasDefaultWrite) {
        return (
          <>
            <H5 style={{ marginTop: '20px' }}>Route-specific rate limits</H5>
            <Table>
              <tbody>
                {rrl.defaultReadLimit !== undefined && rrl.defaultReadLimit !== null && (
                  <tr>
                    <td>Default read limit</td>
                    <td>{rrl.defaultReadLimit} req / 10s</td>
                  </tr>
                )}
                {rrl.defaultWriteLimit !== undefined && rrl.defaultWriteLimit !== null && (
                  <tr>
                    <td>Default write limit</td>
                    <td>{rrl.defaultWriteLimit} req / 10s</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        );
      }
    }
    return null;
  }
  return (
    <>
      <H5 style={{ marginTop: '20px' }}>Route-specific rate limits</H5>
      <Table>
        <thead>
          <tr>
            <td>Routes</td>
            <td>Rate limit</td>
            <td>Rule</td>
          </tr>
        </thead>
        <tbody>
          {(rrl as RouteRateLimitsData).routeLimits.map((rule, i) => (
            <tr key={rule.ruleId ?? rule.ruleName ?? `${rule.routes.join('|')}:${rule.rateLimit}:${i}`}>
              <td>{rule.routes.join(', ') || '—'}</td>
              <td>{rule.rateLimit} req / 10s</td>
              <td>{rule.ruleName ?? rule.ruleId ?? '—'}</td>
            </tr>
          ))}
          {rrl.defaultReadLimit !== undefined && rrl.defaultReadLimit !== null && (
            <tr>
              <td>Default read limit</td>
              <td colSpan={2}>{rrl.defaultReadLimit} req / 10s</td>
            </tr>
          )}
          {rrl.defaultWriteLimit !== undefined && rrl.defaultWriteLimit !== null && (
            <tr>
              <td>Default write limit</td>
              <td colSpan={2}>{rrl.defaultWriteLimit} req / 10s</td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );
}

function renderIntegrations(generalData?: GeneralAccountInfoResponse | null, request?: RequestState): ReactNode {
  if (request && request.error) {
    return (
      <>
        <H5 style={{ marginTop: '20px' }}>Integrations</H5>
        <Text className="error-message">Unable to load integrations: {request.error.message}</Text>
      </>
    );
  }
  // Integrations only render once they load with data, so there's no loading
  // state to flash before an empty result hides the section.
  if (request && request.isFetching) {
    return null;
  }
  const integrations = generalData?.integrations;
  if (!integrations || integrations.length === 0) {
    return null;
  }
  return (
    <>
      <H5 style={{ marginTop: '20px' }}>Integrations</H5>
      <Table>
        <thead>
          <tr>
            <td>Integration</td>
            <td>Enabled</td>
          </tr>
        </thead>
        <tbody>
          {integrations.map((item: IntegrationItem, i: number) => {
            let enabledLabel = '—';
            if (item.enabled === true) {
              enabledLabel = 'Yes';
            } else if (item.enabled === false) {
              enabledLabel = 'No';
            }
            return (
              <tr key={item.name ?? `integration-${i}`}>
                <td>{item.name ?? '—'}</td>
                <td>{enabledLabel}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

// Data Export comes from the account's data-export summary on the general-info
// response (the support-internal get-general lambda rolls it up from the
// warehouse, TSS-1079). dataExport.isConfigured is the "data export enabled for
// the customer" signal; the section lists the configured destinations. It always
// renders (like the other Account Support sections) so account teams don't mistake
// an absent section for a broken page, showing "No data export destinations found"
// when nothing is configured. Loading follows the general-info request.
function renderDataExportDestinations(dataExport?: DataExportSummary | null, request?: RequestState): ReactNode {
  const heading = <H5 style={{ marginTop: '20px' }}>Data Export destinations</H5>;

  if (request && request.error) {
    return (
      <>
        {heading}
        <Text className="error-message">Unable to load data export destinations: {request.error.message}</Text>
      </>
    );
  }

  if (request && request.isFetching) {
    return (
      <>
        {heading}
        <Text>Loading data export destinations...</Text>
      </>
    );
  }

  // Surface whether Data Export is enabled for the customer (dataExport.isConfigured),
  // so support/CSMs can tell "enabled but no destinations yet" apart from "not enabled",
  // instead of both showing only "No data export destinations found". Only shown when
  // the lambda actually returned a summary, so we never claim a state we don't know.
  const enabledStatus = dataExport ? (
    <Flex alignItems="center" gap="8px" mt="4px">
      <Text>Data Export enabled:</Text>
      <BooleanTag value={dataExport.isConfigured} />
    </Flex>
  ) : null;

  const destinations = dataExport?.destinations ?? [];
  if (destinations.length === 0) {
    return (
      <>
        {heading}
        {enabledStatus}
        <Text>No data export destinations found</Text>
      </>
    );
  }

  return (
    <>
      {heading}
      {enabledStatus}
      <Table>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Type</th>
            <th scope="col">Environment</th>
          </tr>
        </thead>
        <tbody>
          {destinations.map((destination, i) => {
            // Prefer a stable key from the destination's own fields so rows don't
            // remount (and lose state) if the list is ever reordered; fall back to
            // the index only when a destination has none of these fields.
            const stableKey =
              [destination.environmentId, destination.name, destination.kind].filter(Boolean).join(':') ||
              `destination-${i}`;
            return (
              <tr key={stableKey}>
                <td>{destination.name || 'Unnamed'}</td>
                <td>{destination.kind ? getDataExportDestinationKindName(destination.kind) : 'Unknown'}</td>
                <td>{destination.environmentId || 'Unknown'}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

function renderSupportEntitlement(generalData?: GeneralAccountInfoResponse | null, request?: RequestState): ReactNode {
  if (request && request.error) {
    return (
      <>
        <H5 style={{ marginTop: '20px' }}>Support entitlement</H5>
        <Text className="error-message">Unable to load support entitlement: {request.error.message}</Text>
      </>
    );
  }
  if (request && request.isFetching) {
    return (
      <>
        <H5 style={{ marginTop: '20px' }}>Support entitlement</H5>
        <Text>Loading support entitlement...</Text>
      </>
    );
  }
  const ent = generalData?.supportEntitlement;
  if (ent === null || ent === undefined || typeof ent !== 'object') {
    return null;
  }
  const entries = Object.entries(ent);
  if (entries.length === 0) {
    return null;
  }
  return (
    <>
      <H5 style={{ marginTop: '20px' }}>Support entitlement</H5>
      <Table>
        <thead>
          <tr>
            <td>Field</td>
            <td>Value</td>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key}>
              <td>{key}</td>
              <td>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

// Shared renderer for the account "limit increases" sections (TSS-1181). Each
// entry is a limit the account has raised above the flag's live default, computed
// by the support-internal get-general lambda, so the list is already filtered to
// increases; each row shows the current limit and the default for context.
// UNLIMITED (-1) renders as ∞ via formatLimit. The section always renders its
// heading (matching the other Account Support sections) and falls back to a
// "not found" message when there are none. If the request failed it surfaces the
// error instead of the misleading "not found" message.
function renderLimitIncreasesSection(
  config: { heading: string; resourceColumn: string; loadingMessage: string; emptyMessage: string; errorLabel: string },
  limits?: readonly AccountLimitIncrease[] | null,
  request?: RequestState,
): ReactNode {
  const heading = <H5 style={{ marginTop: '20px' }}>{config.heading}</H5>;

  if (request && request.error) {
    return (
      <>
        {heading}
        <Text className="error-message">
          {config.errorLabel}: {request.error.message}
        </Text>
      </>
    );
  }

  if (request && request.isFetching) {
    return (
      <>
        {heading}
        <Text>{config.loadingMessage}</Text>
      </>
    );
  }

  const items = limits ?? [];
  if (items.length === 0) {
    return (
      <>
        {heading}
        <Text>{config.emptyMessage}</Text>
      </>
    );
  }

  return (
    <>
      {heading}
      <Table>
        <thead>
          <tr>
            <th scope="col">{config.resourceColumn}</th>
            <th scope="col">Limit</th>
            <th scope="col">Default</th>
          </tr>
        </thead>
        <tbody>
          {items.map((limit) => (
            <tr key={limit.flagKey}>
              <td>{limit.name}</td>
              <td>{formatLimit(limit.value)}</td>
              <td>{formatLimit(limit.default)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

// Account-scoped `resource-count-*` limits raised above their live default.
function renderResourceLimits(limits?: readonly AccountLimitIncrease[] | null, request?: RequestState): ReactNode {
  return renderLimitIncreasesSection(
    {
      heading: 'Account Resource Limits',
      resourceColumn: 'Resource',
      loadingMessage: 'Loading account resource limits...',
      emptyMessage: 'No custom account resource limits found',
      errorLabel: 'Unable to load account resource limits',
    },
    limits,
    request,
  );
}

function formatRateLimit(rateLimit: number | null): string {
  if (rateLimit === -1) {
    return 'Rate limiting disabled';
  }
  if (rateLimit === null || rateLimit === undefined) {
    return '—';
  }
  return `${rateLimit} req / 10s`;
}

// Account-scoped API rate-limit grants (accountId-matched rules/targets on the
// per-route rate-limit flag) — the temporary increases support adds per ticket.
function renderRateLimitIncreases(
  increases?: readonly ApiRateLimitIncrease[] | null,
  request?: RequestState,
): ReactNode {
  const heading = <H5 style={{ marginTop: '20px' }}>API &amp; Rate Limit Increases</H5>;

  if (request && request.error) {
    return (
      <>
        {heading}
        <Text className="error-message">Unable to load API and rate limit increases: {request.error.message}</Text>
      </>
    );
  }
  if (request && request.isFetching) {
    return (
      <>
        {heading}
        <Text>Loading API and rate limit increases...</Text>
      </>
    );
  }

  const items = increases ?? [];
  if (items.length === 0) {
    return (
      <>
        {heading}
        <Text>No custom API or rate limit increases found</Text>
      </>
    );
  }

  return (
    <>
      {heading}
      <Table>
        <thead>
          <tr>
            <th scope="col">Description</th>
            <th scope="col">Rate limit</th>
            <th scope="col">Routes</th>
            <th scope="col">Methods</th>
          </tr>
        </thead>
        <tbody>
          {items.map((increase, i) => (
            <tr key={`${increase.description}:${increase.routes.join('|')}:${i}`}>
              <td>{increase.description || '—'}</td>
              <td>{formatRateLimit(increase.rateLimit)}</td>
              <td>{increase.routes.length > 0 ? increase.routes.join(', ') : '—'}</td>
              <td>{increase.methods.length > 0 ? increase.methods.join(', ') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function renderAccountSupport(generalData?: GeneralAccountInfoResponse | null, request?: RequestState): ReactNode {
  const zendeskRows = renderZendesk(generalData, request);
  if (!zendeskRows) {
    return null;
  }
  return (
    <Table>
      <tbody>{zendeskRows}</tbody>
    </Table>
  );
}

function renderSegments(eap: readonly IEapSegment[] = [], request?: RequestState): ReactNode {
  if (request && request.error) {
    return (
      <Text className="error-message">Unable to load early access program segments: {request.error.message}</Text>
    );
  }
  if (request && request.isFetching) {
    return <Text>Loading early access program segments...</Text>;
  }
  if (!eap.length) {
    return <Text>No early access program segments found</Text>;
  }

  return (
    <Table>
      <thead>
        <tr>
          <td>Segment Name</td>
          <td>Description</td>
        </tr>
      </thead>
      <tbody>
        {eap.map((segment) => {
          if (!segment) {
            return null;
          }
          return (
            <tr key={segment.key}>
              <td>
                <a
                  href={`https://app.ld.catamorphic.com/default/production/segments/${segment.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {segment.name}
                </a>
              </td>
              <td>{segment.description || ''}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

export function GeneralAcctInfoPanel({
  generalData,
  launchdarklyRequest,
  zendeskRequest,
  integrationsRequest,
  supportEntitlementRequest,
  dataExportRequest,
  resourceLimits,
  resourceLimitsRequest,
  rateLimitIncreases,
  rateLimitIncreasesRequest,
}: IGeneralAcctInfoPanelProps) {
  // Every section renders unconditionally from its own request/data. There is no
  // shared loading/error gate, so one slow or failing section can't block or take
  // down the others; each shows its own loading/error/empty state.
  return (
    <Flex>
      <Box width={2 / 3}>
        <div style={{ padding: '20px' }}>
          <H3>General Account Information</H3>
          <H5 style={{ marginTop: '20px' }}>Account Support</H5>
          {renderAccountSupport(generalData, zendeskRequest)}

          <div>
            {renderRouteRateLimits(generalData)}
            {renderIntegrations(generalData, integrationsRequest)}
            {renderSupportEntitlement(generalData, supportEntitlementRequest)}
            <H5 style={{ marginTop: '20px' }}>Early Access Program Segments</H5>
            {renderSegments(generalData?.eap ?? [], launchdarklyRequest)}
          </div>

          {/* Account resource limits always render (like the other Account Support
              sections); the enable-vital-resource-count-limits flag only gates the
              data fetch, so this shows the empty state until the flag is on. */}
          {renderResourceLimits(resourceLimits, resourceLimitsRequest)}

          {/* API & rate limit increases render right below the resource limits so
              all of an account's limit increases are shown together. Gated the same
              way (data only fetched when enable-vital-resource-count-limits is on). */}
          {renderRateLimitIncreases(rateLimitIncreases, rateLimitIncreasesRequest)}

          {/* Data Export is its own request now, so it shows its own loading/error/
              empty state and never blocks or is blocked by the other sections. */}
          {renderDataExportDestinations(generalData?.dataExport, dataExportRequest)}
        </div>
      </Box>
    </Flex>
  );
}
